import {
  Box,
  Center,
  Group,
  Menu,
  Pagination,
  SegmentedControl,
  Select,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import {
  IconLayoutGrid,
  IconLayoutList,
  IconSortAscending,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { seriesApi } from "@/api/series";
import { MediaCard } from "@/components/library/MediaCard";
import { SeriesBookTable } from "@/components/series/SeriesBookTable";
import { CoverGridSkeleton } from "@/components/skeletons";
import { useShowSkeleton } from "@/lib/motion/useShowSkeleton";
import {
  selectCanSelectType,
  selectIsSelectionMode,
  useBulkSelectionStore,
} from "@/store/bulkSelectionStore";
import {
  DEFAULT_PAGE_SIZE,
  type SeriesBooksViewMode,
  useLibraryPreferencesStore,
} from "@/store/libraryPreferencesStore";
import { useUserPreferencesStore } from "@/store/userPreferencesStore";
import type { Book } from "@/types";
import { compareBooksByNumber } from "@/utils/bookSort";

interface SeriesBookListProps {
  seriesId: string;
  seriesName: string;
  bookCount: number;
  libraryId: string;
}

type SortOption = {
  value: string;
  label: string;
};

const SORT_OPTIONS: SortOption[] = [
  { value: "number,asc", label: "Number (Ascending)" },
  { value: "number,desc", label: "Number (Descending)" },
  { value: "title,asc", label: "Title (A-Z)" },
  { value: "title,desc", label: "Title (Z-A)" },
  { value: "created_at,desc", label: "Recently Added" },
  { value: "release_date,desc", label: "Release Date (Newest)" },
  { value: "release_date,asc", label: "Release Date (Oldest)" },
];

const PAGE_SIZES = [25, 50, 100, 200];
const SERIES_BOOKS_TAB = "series-books";
const DEFAULT_SORT = "number,asc";
const DEFAULT_VIEW_MODE: SeriesBooksViewMode = "card";

export function SeriesBookList({
  seriesId,
  seriesName: _seriesName,
  bookCount,
  libraryId,
}: SeriesBookListProps) {
  const [page, setPage] = useState(1);

  // Read persisted preferences from the store (per-library)
  const storedPrefs = useLibraryPreferencesStore((state) =>
    state.getTabPreferences(libraryId, SERIES_BOOKS_TAB),
  );
  const setTabPreferences = useLibraryPreferencesStore(
    (state) => state.setTabPreferences,
  );

  const pageSize = storedPrefs?.pageSize ?? DEFAULT_PAGE_SIZE;
  const sort = storedPrefs?.sort ?? DEFAULT_SORT;
  const viewMode = storedPrefs?.viewMode ?? DEFAULT_VIEW_MODE;

  const setPageSize = useCallback(
    (newPageSize: number) => {
      setTabPreferences(libraryId, SERIES_BOOKS_TAB, { pageSize: newPageSize });
      setPage(1);
    },
    [libraryId, setTabPreferences],
  );

  const setSort = useCallback(
    (newSort: string) => {
      setTabPreferences(libraryId, SERIES_BOOKS_TAB, { sort: newSort });
      setPage(1);
    },
    [libraryId, setTabPreferences],
  );

  const setViewMode = useCallback(
    (newMode: SeriesBooksViewMode) => {
      setTabPreferences(libraryId, SERIES_BOOKS_TAB, { viewMode: newMode });
    },
    [libraryId, setTabPreferences],
  );

  // Bulk selection state - use stable selectors to minimize re-renders
  const isSelectionMode = useBulkSelectionStore(selectIsSelectionMode);
  const canSelectBooks = useBulkSelectionStore(selectCanSelectType("book"));
  const toggleSelection = useBulkSelectionStore(
    (state) => state.toggleSelection,
  );
  const selectRange = useBulkSelectionStore((state) => state.selectRange);
  const getLastSelectedIndex = useBulkSelectionStore(
    (state) => state.getLastSelectedIndex,
  );
  // Get the Set directly for O(1) lookups - only re-renders when the Set changes
  const selectedIds = useBulkSelectionStore((state) => state.selectedIds);
  const setPageItems = useBulkSelectionStore((state) => state.setPageItems);

  // Grid ID for range selection tracking
  const gridId = `series-books-${seriesId}`;

  // Ref for storing books data for range selection (updated after query)
  const booksDataRef = useRef<Book[]>([]);

  // Get show deleted preference from user preferences store
  const showDeletedBooks = useUserPreferencesStore((state) =>
    state.getPreference("library.show_deleted_books"),
  );

  // Fetch books for this series
  const {
    data: allBooks,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["series-books", seriesId, showDeletedBooks],
    queryFn: () =>
      seriesApi.getBooks(seriesId, { includeDeleted: showDeletedBooks }),
  });

  const showSkeleton = useShowSkeleton(isLoading);

  // Sort and paginate client-side since the API returns all books
  const sortedBooks = useMemo(() => {
    if (!allBooks) return [];
    const books = [...allBooks];
    const [field, direction] = sort.split(",");
    books.sort((a, b) => {
      // Number ordering owns its own direction so books still awaiting the
      // renumber pass stay at the end rather than flipping to the top.
      if (field === "number") {
        return compareBooksByNumber(
          a,
          b,
          direction === "desc" ? "desc" : "asc",
        );
      }

      let comparison = 0;
      switch (field) {
        case "title":
          comparison = a.title.localeCompare(b.title);
          break;
        case "created_at":
          comparison =
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
        case "release_date":
          // Fall back to createdAt since releaseDate is not in BookDto
          comparison =
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
        default:
          comparison = 0;
      }
      return direction === "desc" ? -comparison : comparison;
    });
    return books;
  }, [allBooks, sort]);

  const paginatedBooks = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sortedBooks.slice(start, start + pageSize);
  }, [sortedBooks, page, pageSize]);

  const data = useMemo(
    () => ({
      data: paginatedBooks,
      total: sortedBooks.length,
    }),
    [paginatedBooks, sortedBooks.length],
  );

  // Update booksDataRef when data changes (for range selection)
  if (paginatedBooks) {
    booksDataRef.current = paginatedBooks;
  }

  // Register visible page items for Select All functionality
  useEffect(() => {
    if (paginatedBooks && paginatedBooks.length > 0) {
      setPageItems({
        ids: paginatedBooks.map((b) => b.id),
        type: "book",
      });
    } else {
      setPageItems(null);
    }
    return () => setPageItems(null);
  }, [paginatedBooks, setPageItems]);

  const totalPages = data ? Math.ceil(data.total / pageSize) : 1;

  // Handle selection with shift+click range support
  const handleSelect = useCallback(
    (id: string, shiftKey: boolean, index?: number) => {
      if (shiftKey && isSelectionMode && index !== undefined) {
        // Shift+click: select range from last selected to current
        const lastIndex = getLastSelectedIndex(gridId);
        if (lastIndex !== undefined && lastIndex !== index) {
          const start = Math.min(lastIndex, index);
          const end = Math.max(lastIndex, index);
          const rangeIds = booksDataRef.current
            .slice(start, end + 1)
            .map((item) => item.id);
          selectRange(rangeIds, "book");
          return;
        }
      }
      // Normal click: toggle selection
      toggleSelection(id, "book", gridId, index);
    },
    [
      toggleSelection,
      selectRange,
      getLastSelectedIndex,
      gridId,
      isSelectionMode,
    ],
  );

  const currentSortLabel =
    SORT_OPTIONS.find((opt) => opt.value === sort)?.label || "Sort";

  if (error) {
    return (
      <Stack gap="md">
        <Title order={4}>Books ({bookCount})</Title>
        <Text c="red">Failed to load books</Text>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <Title order={4}>Books ({bookCount})</Title>

        <Group gap="sm">
          <Menu shadow="md" width={200}>
            <Menu.Target>
              <Box
                style={{ cursor: "pointer" }}
                component="button"
                bg="transparent"
                bd="none"
              >
                <Group gap="xs">
                  <IconSortAscending size={16} />
                  <Text size="sm">{currentSortLabel}</Text>
                </Group>
              </Box>
            </Menu.Target>
            <Menu.Dropdown>
              {SORT_OPTIONS.map((option) => (
                <Menu.Item
                  key={option.value}
                  onClick={() => setSort(option.value)}
                  style={{
                    fontWeight: sort === option.value ? 600 : 400,
                  }}
                >
                  {option.label}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>

          <Select
            size="xs"
            w={80}
            value={pageSize.toString()}
            onChange={(value) => {
              if (value) {
                setPageSize(parseInt(value, 10));
              }
            }}
            data={PAGE_SIZES.map((size) => ({
              value: size.toString(),
              label: size.toString(),
            }))}
          />

          <SegmentedControl
            size="xs"
            value={viewMode}
            onChange={(value) => setViewMode(value as SeriesBooksViewMode)}
            aria-label="View mode"
            data={[
              {
                value: "card",
                label: (
                  <Center style={{ gap: 4 }}>
                    <IconLayoutGrid size={14} />
                    <Box visibleFrom="sm">Cards</Box>
                  </Center>
                ),
              },
              {
                value: "table",
                label: (
                  <Center style={{ gap: 4 }}>
                    <IconLayoutList size={14} />
                    <Box visibleFrom="sm">Table</Box>
                  </Center>
                ),
              },
            ]}
          />
        </Group>
      </Group>

      {isLoading ? (
        showSkeleton ? (
          <CoverGridSkeleton count={Math.min(pageSize, 12)} exactCount />
        ) : null
      ) : data?.data.length === 0 ? (
        <Text c="dimmed">No books in this series</Text>
      ) : (
        <>
          {viewMode === "table" ? (
            <SeriesBookTable
              books={data?.data ?? []}
              onSelect={handleSelect}
              selectedIds={selectedIds}
              isSelectionMode={isSelectionMode}
              canBeSelected={canSelectBooks}
            />
          ) : (
            <div
              data-stagger-grid="true"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
                gap: "var(--mantine-spacing-md)",
                width: "100%",
              }}
            >
              {data?.data.map((book, index) => (
                <MediaCard
                  key={book.id}
                  type="book"
                  data={book}
                  hideSeriesName
                  index={index}
                  onSelect={handleSelect}
                  isSelected={selectedIds.has(book.id)}
                  isSelectionMode={isSelectionMode}
                  canBeSelected={canSelectBooks}
                />
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <Center mt="md">
              <Pagination
                value={page}
                onChange={setPage}
                total={totalPages}
                siblings={1}
                boundaries={1}
              />
            </Center>
          )}
        </>
      )}
    </Stack>
  );
}
